import { useState } from 'react';
import { PhoneOff, Plus, X } from 'lucide-react';
import type { WorkflowGraph, WorkflowNode, WorkflowStep } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  addBranch,
  branchLabel,
  defaultExits,
  endingExits,
  laterSteps,
  nextOf,
  removeBranch,
  setBranchTarget,
  setOtherwise,
  type StepExit,
} from './graph';

/**
 * "What happens next", in words: one row per way the step can end, each with
 * the step the call goes to (ADR-276). This is the same thing the arrows on the
 * canvas show, for someone who would rather read it than follow lines.
 *
 * The usual row is the default — every ending nobody gave a row of its own.
 * An author sends an ending somewhere else by adding a row for it; removing
 * the row sends it back the usual way. Only steps later in the call are
 * offered, so a call can never be sent round in a loop.
 */

/** The value the target pickers use for "make a new step here". */
const NEW_STEP = '__new__';
/** The value for "nothing": the call ends after this step. */
const END_CALL = '__end__';

/** Which arrow of the step a new step would go on. */
export type ArrowSlot = number | 'otherwise' | { outcome: string };

export function NextSteps({
  graph,
  node,
  exits,
  labels,
  steps,
  busy,
  onChange,
  onNewStep,
}: {
  graph: WorkflowGraph;
  node: WorkflowNode;
  exits: readonly StepExit[];
  /** Every ending the step declares, to name a branch an author routed by hand. */
  labels: readonly StepExit[];
  steps: ReadonlyMap<string, WorkflowStep>;
  busy: boolean;
  onChange: (graph: WorkflowGraph) => void;
  /** Open "Add a step" for this arrow. */
  onNewStep: (slot: ArrowSlot) => void;
}): React.ReactElement {
  /** An ending the author has picked a row for, whose step they have not chosen yet. */
  const [pending, setPending] = useState<string | null>(null);
  const next = nextOf(node);
  const rest = defaultExits(node, exits);
  const ending = endingExits(node, exits);
  const targets = laterSteps(graph, node.id)
    .filter((n) => n.id !== node.id)
    .map((n) => ({ id: n.id, name: steps.get(n.stepId)?.name ?? n.id }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const target = (
    slot: number | 'otherwise',
    value: string | null,
    options: { allowEnd: boolean }
  ): React.ReactElement => (
    <Select
      value={value ?? END_CALL}
      disabled={busy}
      onValueChange={(v) => {
        if (v === NEW_STEP) return onNewStep(slot);
        if (slot === 'otherwise') onChange(setOtherwise(graph, node.id, v === END_CALL ? null : v));
        else onChange(setBranchTarget(graph, node.id, slot, v));
      }}
    >
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.allowEnd && (
          <SelectItem value={END_CALL}>
            <span className="flex items-center gap-2">
              <PhoneOff className="h-3.5 w-3.5" />
              The call ends
            </span>
          </SelectItem>
        )}
        {targets.map((t) => (
          <SelectItem key={t.id} value={t.id}>
            {t.name}
          </SelectItem>
        ))}
        <SelectSeparator />
        <SelectItem value={NEW_STEP}>
          <span className="flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" />A new step…
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );

  // A step with one way out (a line said, a silent lookup) just goes on.
  if (exits.length <= 1 && next.branches.length === 0) {
    return (
      <div className="space-y-1.5">
        <div className="text-xs text-muted-foreground">Then</div>
        {target('otherwise', next.otherwise, { allowEnd: true })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {ending.map((e) => (
        <div key={e.id} className="space-y-1.5">
          <div className="text-xs">
            When: <span className="font-medium">{e.label}</span>
          </div>
          <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <PhoneOff className="h-3.5 w-3.5" />
            The call ends here: they are with a person now
          </div>
        </div>
      ))}
      {next.branches.map((b, i) => (
        <div key={`${b.to}-${i}`} className="space-y-1.5">
          <div className="text-xs">
            When: <span className="font-medium">{branchLabel(b, labels)}</span>
          </div>
          <div className="flex items-center gap-1">
            {target(i, b.to, { allowEnd: false })}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Send this the usual way instead"
              title="Send this the usual way instead"
              disabled={busy}
              onClick={() => onChange(removeBranch(graph, node.id, i))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      {(next.otherwise !== null || rest.length > 0) && (
        <div className="space-y-1.5">
          <div className="text-xs">
            {rest.length === 1 ? (
              <>
                When: <span className="font-medium">{rest[0]!.label}</span>
              </>
            ) : (
              <>
                <span className="font-medium">
                  {next.branches.length ? 'Any other way' : 'However it ends'}
                </span>
                {rest.length > 0 && (
                  <span className="block text-muted-foreground">
                    {rest.map((e) => e.label).join(', ')}
                  </span>
                )}
              </>
            )}
          </div>
          {target('otherwise', next.otherwise, { allowEnd: next.branches.length === 0 })}
        </div>
      )}
      {pending && (
        // Not saved until it has somewhere of its own to go: a row pointing
        // where the rest already go would be an arrow nobody can see.
        <div className="space-y-1.5">
          <div className="text-xs">
            When: <span className="font-medium">{exits.find((e) => e.id === pending)?.label}</span>
          </div>
          <div className="flex items-center gap-1">
            <Select
              value=""
              disabled={busy}
              onValueChange={(to) => {
                setPending(null);
                if (to === NEW_STEP) return onNewStep({ outcome: pending });
                onChange(addBranch(graph, node.id, pending, to));
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose where it goes…" />
              </SelectTrigger>
              <SelectContent>
                {targets
                  .filter((t) => t.id !== next.otherwise)
                  .map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                <SelectSeparator />
                <SelectItem value={NEW_STEP}>
                  <span className="flex items-center gap-2">
                    <Plus className="h-3.5 w-3.5" />A new step…
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Cancel"
              disabled={busy}
              onClick={() => setPending(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      {!pending && rest.length >= 2 && next.otherwise && (
        <Select value="" disabled={busy} onValueChange={(outcome) => setPending(outcome)}>
          <SelectTrigger className="w-full border-dashed text-muted-foreground">
            <SelectValue placeholder="Send one ending somewhere else…" />
          </SelectTrigger>
          <SelectContent>
            {rest.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                When: {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <p className="text-xs text-muted-foreground">
        Only steps later in the call are listed. Each step already tries again on its own, so a call
        never needs to go back.
      </p>
    </div>
  );
}
